import { clsx } from 'clsx';
import { ComponentProps, ComponentPropsWithRef, FC } from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import {
  drawerContent,
  drawerContentHandle,
  drawerContentVariants,
  drawerDescription,
  drawerFooter,
  drawerHeader,
  drawerOverlay,
  drawerTitle,
} from './drawer.css.ts';

const Drawer = ({ ...props }: ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root noBodyStyles={true} {...props} />
);

const DrawerTrigger: FC<
  ComponentPropsWithRef<typeof DrawerPrimitive.Trigger>
> = ({ children, ...props }) => (
  <DrawerPrimitive.Trigger {...props}>{children}</DrawerPrimitive.Trigger>
);

export const DrawerClose: FC<
  ComponentPropsWithRef<typeof DrawerPrimitive.Close>
> = ({ children, ...props }) => (
  <DrawerPrimitive.Close {...props} style={{ all: 'unset' }}>
    {children}
  </DrawerPrimitive.Close>
);

const DrawerOverlay = function DrawerOverlayComponent({
  ref,
  className,
  ...props
}: ComponentPropsWithRef<typeof DrawerPrimitive.Overlay>) {
  return (
    <DrawerPrimitive.Overlay
      ref={ref}
      className={clsx(drawerOverlay, className)}
      {...props}
    />
  );
};

interface DrawerContentProps extends ComponentPropsWithRef<
  typeof DrawerPrimitive.Content
> {
  inversed?: boolean;
  withHandle?: boolean;
  direction?: 'top' | 'right' | 'bottom' | 'left';
  withOverlay?: boolean;
}

const DrawerContent = function DrawerContentComponent({
  ref,
  className,
  children,
  inversed,
  withHandle = true,
  direction = 'bottom',
  withOverlay = true,
  ...props
}: DrawerContentProps) {
  return (
    <DrawerPrimitive.Portal>
      {withOverlay ? <DrawerOverlay /> : null}
      <DrawerPrimitive.Content
        ref={ref}
        className={clsx(
          drawerContent,
          drawerContentVariants[direction],
          className,
        )}
        data-inversed={inversed ? '' : undefined}
        {...props}
      >
        {withHandle ? (
          <DrawerPrimitive.Handle
            className={drawerContentHandle}
            data-inversed={inversed ? '' : undefined}
          />
        ) : null}
        {children}
      </DrawerPrimitive.Content>
    </DrawerPrimitive.Portal>
  );
};

const DrawerHeader = ({
  className,
  ...props
}: ComponentPropsWithRef<'div'>) => (
  <div className={clsx(drawerHeader, className)} {...props} />
);

const DrawerFooter = ({
  className,
  ...props
}: ComponentPropsWithRef<'div'>) => (
  <div className={clsx(drawerFooter, className)} {...props} />
);

const DrawerTitle = function DrawerTitleComponent({
  ref,
  className,
  ...props
}: ComponentPropsWithRef<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      ref={ref}
      className={clsx(drawerTitle, className)}
      {...props}
    />
  );
};

const DrawerDescription = function DrawerDescriptionComponent({
  ref,
  className,
  ...props
}: ComponentPropsWithRef<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      ref={ref}
      className={clsx(drawerDescription, className)}
      {...props}
    />
  );
};

export {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerTitle,
  DrawerTrigger,
};
